// Client-side mirror of the backend v2 compute_estimate in
// /app/backend/app/routes/mms.py. The engine is hours-first: labels return
// HOURS, then the backend converts to € via `hourly_rate`. This file
// implements the same math so the UI updates instantly on every field edit.

const find = (arr, key) => (arr || []).find((it) => it && it.key === key) || null;
const findSub = (taxonomy, key) => {
  for (const cat of taxonomy || []) {
    for (const sub of cat.subcategories || []) if (sub.key === key) return sub;
  }
  return null;
};
const toFloat = (v, fb = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fb;
};
const toInt = (v, fb = 0) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : fb;
};
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const mid = (r, fb = 0) => (Array.isArray(r) && r.length === 2 ? (toFloat(r[0]) + toFloat(r[1])) / 2 : toFloat(r, fb));

function quantityMultiplier(cfg, qty, complexity) {
  const table = (cfg.quantity_multipliers || []).slice().sort((a, b) => toInt(a.qty) - toInt(b.qty));
  if (!table.length) return 1;
  const col = ["low", "medium", "high", "very_high"].includes(complexity) ? complexity : "medium";
  if (qty <= toInt(table[0].qty)) return toFloat(table[0][col], 1);
  if (qty >= toInt(table[table.length - 1].qty)) return toFloat(table[table.length - 1][col], 1);
  for (let i = 0; i < table.length - 1; i++) {
    const a = table[i], b = table[i + 1];
    const qa = toInt(a.qty), qb = toInt(b.qty);
    if (qa <= qty && qty <= qb) {
      const va = toFloat(a[col], 1), vb = toFloat(b[col], 1);
      return qa === qb ? va : va + (vb - va) * ((qty - qa) / (qb - qa));
    }
  }
  return 1;
}

function sumModifierHours(cfgMods, selected, leg) {
  if (!selected?.length) return 0;
  const byKey = Object.fromEntries((cfgMods || []).map((m) => [m.key, m]));
  let total = 0;
  for (const s of selected) {
    const mod = byKey[s.key];
    if (!mod) continue;
    const count = toFloat(s.count, 1);
    if (mod.is_range) {
      const chosen = s[`${leg}_hours`];
      const val = chosen != null ? toFloat(chosen, mid(mod[`${leg}_range`])) : mid(mod[`${leg}_range`]);
      total += val * count;
    } else {
      total += toFloat(mod[leg], 0) * count;
    }
  }
  return total;
}

function handlingMultiplier(cfg, key, multiple) {
  const entry = find(cfg.material_handling, key);
  let base = 1;
  if (entry) base = entry.multiplier != null ? toFloat(entry.multiplier, 1) : mid(entry.multiplier_range, 1);
  if (multiple) base += mid(cfg.multiple_materials_addition_range);
  return base;
}

function applyPatternSource(hours, cfg, sourceKey, override) {
  const src = find(cfg.pattern_sources, sourceKey);
  if (!src) return hours;
  if ("pattern_hours_override" in src) {
    const review = override != null ? toFloat(override, mid(src.review_hours_range)) : mid(src.review_hours_range);
    return Math.max(0, review);
  }
  if (src.correction_hours_only) return Math.max(0, toFloat(override, 0));
  if ("pattern_multiplier" in src) return hours * toFloat(src.pattern_multiplier, 1);
  if ("pattern_multiplier_range" in src) {
    const m = override != null ? toFloat(override, mid(src.pattern_multiplier_range, 1)) : mid(src.pattern_multiplier_range, 1);
    return hours * m;
  }
  return hours;
}

function accessoryRange(cfg, selected, rate, qty) {
  const byKey = Object.fromEntries((cfg.accessories || []).map((a) => [a.key, a]));
  let lo = 0, hi = 0;
  for (const s of selected || []) {
    const acc = byKey[s.key]; if (!acc) continue;
    const qtyPer = toFloat(s.quantity_per_garment, toFloat(acc.quantity_default, 1));
    const per = toFloat(acc.unit_cost, 0) * qtyPer + toFloat(acc.application_time, 0) * rate;
    lo += toFloat(acc.setup_cost, 0) + per * qty;
    hi += toFloat(acc.setup_cost, 0) + per * qty;
  }
  return { min: lo, max: hi };
}

function processingRange(cfg, selected, rate, qty) {
  const byKey = Object.fromEntries((cfg.processing_techniques || []).map((t) => [t.key, t]));
  let lo = 0, hi = 0;
  for (const s of selected || []) {
    const t = byKey[s.key]; if (!t) continue;
    const [uLo, uHi] = t.unit_cost_range || [0, 0];
    const setup = toFloat(t.setup_cost, 0);
    const appl = toFloat(t.application_time, 0) * rate;
    lo += setup + (toFloat(uLo, 0) + appl) * qty;
    hi += setup + (toFloat(uHi, 0) + appl) * qty;
  }
  return { min: lo, max: hi };
}

export function computeEstimate(inputs, cfg) {
  if (!cfg) return null;
  const rate = toFloat(cfg.hourly_rate, 20);
  const g = inputs?.garment || {};
  const pIn = inputs?.pattern || {};
  const cIn = inputs?.construction || {};
  const mIn = inputs?.manual_work || [];
  const matIn = inputs?.materials || {};
  const accIn = inputs?.accessories || [];
  const procIn = inputs?.processing || [];
  const prodIn = inputs?.production || {};

  const qty = Math.max(0, toInt(prodIn.quantity, 0));
  const complexity = (prodIn.complexity || g.complexity || "medium").toLowerCase();

  const sub = findSub(cfg.taxonomy, g.subcategory) || {};
  const base = sub.hours || { pattern: 0, sample: 0, production_unit: 0 };

  const modPattern = sumModifierHours(cfg.construction_modifiers, cIn.modifiers, "pattern");
  const modSample = sumModifierHours(cfg.construction_modifiers, cIn.modifiers, "sample");
  const modProd = sumModifierHours(cfg.construction_modifiers, cIn.modifiers, "production");

  let manualHours = 0;
  for (const e of mIn || []) {
    if (e.hours != null) { manualHours += toFloat(e.hours, 0); continue; }
    const b = find(cfg.manual_work_brackets, e.key);
    if (b?.range) manualHours += mid(b.range);
  }

  const handling = handlingMultiplier(cfg, matIn.handling_key, !!matIn.multiple_materials);

  const patternRaw = toFloat(base.pattern, 0) + modPattern;
  const patternHours = applyPatternSource(patternRaw, cfg, pIn.source, pIn.override_hours);
  const patternCost = patternHours * rate;

  const sampleHours = (toFloat(base.sample, 0) + modSample + manualHours) * handling;
  const sampleCost = sampleHours * rate;

  const prodUnitRaw = (toFloat(base.production_unit, 0) + modProd) * handling;
  const qMult = qty > 0 ? quantityMultiplier(cfg, qty, complexity) : 1;
  const prodUnit = prodUnitRaw * qMult;
  const productionTotal = prodUnit * rate * qty;
  const perUnitCost = prodUnit * rate;

  const cr = matIn.cost_range || { min: 0, max: 0 };
  const matMin = toFloat(cr.min, 0);
  const matMax = toFloat(cr.max, matMin);

  const acc = accessoryRange(cfg, accIn, rate, qty);
  const proc = processingRange(cfg, procIn, rate, qty);

  const totalMin = patternCost + sampleCost + matMin + acc.min + proc.min + productionTotal;
  const totalMax = patternCost + sampleCost + matMax + acc.max + proc.max + productionTotal;

  return {
    currency: cfg.currency || "EUR",
    hourly_rate: rate,
    quantity: qty,
    complexity,
    hours: {
      pattern: round2(patternHours), sample: round2(sampleHours),
      manual_work: round2(manualHours),
      production_unit_raw: round2(prodUnitRaw), production_unit: round2(prodUnit),
      material_handling_multiplier: round2(handling), quantity_multiplier: round2(qMult),
    },
    pattern_cost: round2(patternCost),
    sample_cost: round2(sampleCost),
    materials: { min: round2(matMin), max: round2(matMax) },
    accessories: { min: round2(acc.min), max: round2(acc.max) },
    processing: { min: round2(proc.min), max: round2(proc.max) },
    production: round2(productionTotal),
    production_per_unit: round2(perUnitCost),
    total: { min: round2(totalMin), max: round2(totalMax) },
    product_development: round2(patternCost + sampleCost),
  };
}

/**
 * Strip the engine estimate down to the commercial figures the client is
 * allowed to see. Never exposes hours, hourly rate, per-phase labour or
 * multipliers. Mirrors the backend `_sanitize_estimate_for_client`.
 */
export function sanitizeEstimateForClient(estimate) {
  if (!estimate) return null;
  const qty = Math.max(0, parseInt(estimate.quantity, 10) || 0);
  const currency = estimate.currency || "EUR";
  const patternCost = toFloat(estimate.pattern_cost, 0);
  const sampleCost = toFloat(estimate.sample_cost, 0);
  const mats = estimate.materials || { min: 0, max: 0 };
  const accs = estimate.accessories || { min: 0, max: 0 };
  const procs = estimate.processing || { min: 0, max: 0 };
  const prodLabour = toFloat(estimate.production, 0);
  const perUnitLabour = toFloat(estimate.production_per_unit, 0);
  const matMax = toFloat(mats.max, 0);
  const accMax = toFloat(accs.max, 0);
  const procMax = toFloat(procs.max, 0);

  const samplePrice = patternCost + sampleCost + matMax;
  const perPieceExtras = qty > 0 ? (accMax + procMax) / qty : 0;
  const productionPerUnit = qty > 0 ? perUnitLabour + perPieceExtras : 0;
  const productionTotal = qty > 0 ? productionPerUnit * qty : 0;

  const exclusions = [];
  if (matMax === 0) exclusions.push("Materiali");
  if (accMax === 0) exclusions.push("Accessori");
  if (procMax === 0) exclusions.push("Stampe e lavorazioni esterne");

  return {
    currency,
    quantity: qty,
    sample_price: round2(samplePrice),
    production_price_per_unit: round2(productionPerUnit),
    production_total: round2(productionTotal),
    total: round2(samplePrice + productionTotal),
    exclusions,
  };
}

/**
 * Live client-side estimate used by MmsSimulatorPage — matches the backend
 * `_sanitize_estimate_for_client(compute_estimate(...))` output exactly.
 */
export function computeClientEstimate(inputs, cfg) {
  const full = computeEstimate(inputs, cfg);
  return sanitizeEstimateForClient(full);
}

export function formatCurrency(amount, currency = "EUR") {
  const n = Number.isFinite(amount) ? amount : 0;
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${currency} ${n.toFixed(0)}`;
  }
}

export function blankInputs() {
  return {
    garment: { primary_category: "", subcategory: "", fit: "regular", complexity: "medium" },
    pattern: { source: "mms_created", override_hours: null },
    construction: { modifiers: [] },
    manual_work: [],
    materials: { handling_key: "cotton_stable", multiple_materials: false, cost_range: { min: 0, max: 0 } },
    accessories: [],
    processing: [],
    production: { quantity: 1, complexity: "medium" },
    ai_analysis: null,
    confidence: {},
    confirmed_fields: [],
    notes: "",
  };
}
